import { Scene, MeshBuilder, Vector3, StandardMaterial, Color3, HemisphericLight, Mesh, InstancedMesh, SceneLoader, Curve3, ShadowGenerator, DirectionalLight, SpotLight, PointLight, AbstractMesh, VertexBuffer, TransformNode } from '@babylonjs/core';
import { PhysicsManager } from './physics';
import { PhysicsShapeType, CollisionGroups, CollisionMasks } from './physics/IPhysicsEngine';
import { UnifiedLightManager } from './UnifiedLightManager';
import { TriggerSystem } from './TriggerSystem';
import { PostProcessManager } from './PostProcessManager';
import { ParticleManager } from './ParticleManager';
import { EnvironmentManager } from './EnvironmentManager';
import { DynamicObjectSystem } from './DynamicObjectSystem';
import { SpatialCullingManager } from './SpatialCullingManager';
import { BehaviorRegistry } from '../core/BehaviorRegistry';
import { logger } from '../core/Logger';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { StateManager } from '../state/StateManager';
import { EntityType } from '../types/EntityTypes';
import { createActor } from 'xstate';
import { CameraConfig } from './camera/CameraConfig';
import { extractVideoConfigFromMesh, applyVideoMaterialToMesh, playVideoOnMesh } from '../assets/VideoTextureUtils';
import '@babylonjs/loaders/glTF'; // Import GLTF loader
import { getWorldPosition, getWorldTransform, prepareDebrisForSpawning } from './utils/MeshTransformUtils';

// MUST MATCH Blender addon (blender/himum_tools/schema.py)
const ENTITY_PROPERTY_PREFIX = 'hm';

// Debug layer mask - meshes with this layer mask should never cast shadows
const DEBUG_LAYER_MASK = 0x20000000;

/**
 * Configure a ShadowGenerator to exclude debug meshes globally.
 * Uses customAllowRendering callback to check layer mask at render time,
 * so debug meshes created AFTER the shadow generator are still excluded.
 */
/**
 * Check if a mesh is a debug visualization mesh that should be excluded from effects
 */
function isDebugMesh(mesh: AbstractMesh): boolean {
    // Check layer mask first (fastest)
    if ((mesh.layerMask & DEBUG_LAYER_MASK) !== 0) {
        return true;
    }
    // Check metadata
    if (mesh.metadata?.isDebug === true || mesh.metadata?.noShadows === true) {
        return true;
    }
    // Check name patterns for debug visualization meshes
    const name = mesh.name;
    return (
        name.startsWith('debug') ||
        name.startsWith('angleArc') ||
        name.startsWith('currentSegment') ||
        name.includes('_debug_') ||
        name.startsWith('gizmo')
    );
}

function configureDebugMeshFilter(shadowGenerator: ShadowGenerator): void {
    // customAllowRendering is called for every submesh on every shadow render
    // Return false to exclude a mesh from shadow casting
    const existingFilter = shadowGenerator.customAllowRendering;
    shadowGenerator.customAllowRendering = (subMesh) => {
        const mesh = subMesh.getMesh();
        // Use unified debug mesh check
        if (isDebugMesh(mesh)) {
            return false; // Exclude from shadows
        }
        // Call existing filter if any
        if (existingFilter) {
            return existingFilter(subMesh);
        }
        return true; // Include by default
    };
}

/**
 * Get entity property from extras using the standardized prefix
 * Converts 'Emission.rate' → 'hm_Emission_rate'
 */
function getEntityProperty<T = any>(extras: any, propPath: string, defaultValue: T): T {
    if (!extras) return defaultValue;
    const key = `${ENTITY_PROPERTY_PREFIX}_${propPath.replace(/\./g, '_')}`;
    return extras[key] ?? defaultValue;
}

/**
 * Get behavior parameter from extras using the behavior prefix
 * Converts 'character_id' → 'hm_behavior_character_id'
 */
function getBehaviorParam<T = any>(extras: any, name: string, defaultValue: T): T {
    if (!extras) return defaultValue;
    const key = `${ENTITY_PROPERTY_PREFIX}_behavior_${name}`;
    return extras[key] ?? defaultValue;
}

export interface LevelConfig {
    mapPath: string;
    mapScale: number;
    createDefaultLight?: boolean;
}

/**
 * NPC spawn data extracted from map markers
 */
export interface NPCMarkerData {
    /** Unique spawn ID (from marker mesh name) */
    id: string;
    /** Character config ID (e.g., 'hotdog', 'robo') */
    characterId: string;
    /** Spawn position from marker */
    position: Vector3;
    /** Radius at which player triggers spawn (0 = always spawned) */
    spawnRadius: number;
    /** Patrol left distance (meters) */
    patrolLeftDistance: number;
    /** Patrol right distance (meters) */
    patrolRightDistance: number;
    /** Patrol wait time at endpoints (seconds) */
    patrolWaitTime: number;
    /** Override max health (if not set, uses character config default) */
    maxHealth?: number;
    /** Override starting health (if not set, defaults to maxHealth) */
    startHealth?: number;
    /** Behavior configuration from map annotations */
    behaviorConfig?: {
        behaviorId?: string;
        movement?: 'patrol' | 'idle' | 'follow' | 'wander';
        combat?: 'passive' | 'aggressive' | 'defensive' | 'territorial';
        attackRange?: number;
        detectionRange?: number;
        attackCooldown?: number;
        patrolLeft?: number;
        patrolRight?: number;
    };
}

export interface LevelData {
    spline: Curve3 | null;
    spawnPoint: Vector3 | null;
    splineSource: 'mesh' | 'data' | 'none';
    mapConfig: MapConfig;
    /** NPC spawn markers found in the map */
    npcSpawns: NPCMarkerData[];
    /** Camera animation groups from the map GLB (for cinematics) */
    cameraAnimations: import('@babylonjs/core').AnimationGroup[];
}

export interface FogConfig {
    enabled: boolean;
    mode: 'linear' | 'exp' | 'exp2';
    density: number;
    start: number;
    end: number;
    color: Vector3;
}

export interface LensConfig {
    enabled: boolean;
    edge_blur: number;
    chromatic_aberration: number;
    distortion: number;
    grain_amount: number;
    dof_focus_distance: number;
    dof_enabled: boolean;
    dof_aperture: number;
    dof_darken: number;
    dof_pentagon: boolean;
    dof_gain: number;
    dof_threshold: number;
}

export interface MapConfig {
    physics?: {
        gravity?: number | Vector3;
    };
    ambient?: Vector3;
    camera?: CameraConfig;
    /** Maximum simultaneous lights (default 8) */
    maxLights?: number;
    /** Name of camera to use for initial view. If animated, plays intro first. */
    startCamera?: string;
    /** Global fog settings */
    fog?: FogConfig;
    /** Lens rendering pipeline settings */
    lens?: LensConfig;
}

export class LevelBuilder {
  private scene: Scene;
  private physicsManager: PhysicsManager;
  private triggerSystem?: TriggerSystem;
  private postProcessManager?: PostProcessManager;
  private environmentManager: EnvironmentManager;
  private dynamicObjectSystem?: DynamicObjectSystem;
  private stateManager?: StateManager;
  private lastLevelData: LevelData | null = null;
  
  // Track loaded resources for cleanup on level unload
  private loadedMeshes: AbstractMesh[] = [];
  private loadedTransformNodes: TransformNode[] = [];
  private loadedAnimationGroups: import('@babylonjs/core').AnimationGroup[] = [];
  private defaultLight: HemisphericLight | null = null;

  constructor(
    scene: Scene, 
    physicsManager: PhysicsManager, 
    triggerSystem?: TriggerSystem, 
    postProcessManager?: PostProcessManager, 
    _particleManager?: ParticleManager, // Kept for API compatibility; behaviors create particles directly
    dynamicObjectSystem?: DynamicObjectSystem,
    stateManager?: StateManager
  ) {
    this.scene = scene;
    this.physicsManager = physicsManager;
    this.triggerSystem = triggerSystem;
    this.postProcessManager = postProcessManager;
    this.dynamicObjectSystem = dynamicObjectSystem;
    this.stateManager = stateManager;
    this.environmentManager = new EnvironmentManager(scene);
  }

  public getLastLevelData(): LevelData | null {
    return this.lastLevelData;
  }

  /**
   * Check if a mesh has collision enabled via Blender custom property
   * Only supports hm_collision property - legacy 'collision' is no longer supported
   * Also checks parent TransformNode for multi-material meshes that get split into primitives
   */
  private hasCollisionProperty(mesh: AbstractMesh): boolean {
    // Check for hm_collision property on the mesh itself
    const extras = this.getExtras(mesh);
    const hmCollision = getEntityProperty(extras, 'collision', null);
    if (hmCollision === true || hmCollision === 'true') {
      logger.level.debug(`   Using hm_collision property for ${mesh.name}`);
      return true;
    }
    
    // For multi-material meshes split into primitives (e.g., Plane.009_primitive0),
    // check parent TransformNode for the collision property
    if (mesh.parent && mesh.name.includes('_primitive')) {
      const parentExtras = this.getExtras(mesh.parent as TransformNode);
      const parentCollision = getEntityProperty(parentExtras, 'collision', null);
      if (parentCollision === true || parentCollision === 'true') {
        logger.level.debug(`   Using hm_collision from parent ${mesh.parent.name} for ${mesh.name}`);
        return true;
      }
    }
    
    return false;
  }

  /**
   * Check if a mesh should be hidden via Blender custom property
   */
  private hasHiddenProperty(mesh: AbstractMesh): boolean {
    if (!mesh.metadata) return false;
    
    // Check various metadata locations for the hm_hidden property
    if (mesh.metadata.gltf?.extras?.hm_hidden === true) return true;
    if (mesh.metadata.extras?.hm_hidden === true) return true;
    if (mesh.metadata.hm_hidden === true) return true;
    
    return false;
  }

    private getExtras(metadataOwner: { metadata?: any } | null | undefined): any {
        if (!metadataOwner?.metadata) return null;
        return (
            metadataOwner.metadata.gltf?.extras ??
            metadataOwner.metadata.extras ??
            metadataOwner.metadata
        );
    }

    /**
     * Process a mesh node via its behavior's onProcessNode hook
     * Returns true if the behavior handled the node (skip further processing)
     */
    private processNodeViaBehavior(
      mesh: AbstractMesh,
      extras: Record<string, unknown>,
      buildResults: { 
        spawnPoint: Vector3 | null; 
        mapConfig: MapConfig | null;
        cullDistance: number;
        npcSpawns: NPCMarkerData[];
      }
    ): { handled: boolean; skipPhysics: boolean } {
      // Get behaviors from hm_behaviors property
      const behaviorsStr = getEntityProperty<string>(extras, 'behaviors', '');
      
      if (!behaviorsStr) return { handled: false, skipPhysics: false };
      
      // Parse behaviors (comma-separated)
      const behaviorIds = behaviorsStr.split(',').map(b => b.trim()).filter(Boolean);
      
      for (const behaviorId of behaviorIds) {
        const resolved = BehaviorRegistry.get(behaviorId, mesh.name);
        if (!resolved) {
          logger.level.warn(`Behavior "${behaviorId}" not found in registry for ${mesh.name}`);
          continue;
        }
        
        const behavior = resolved.behavior;
        if (!behavior.onProcessNode) continue;
        
        // Build context for behavior hook
        // The results object proxies to buildResults with type conversion
        const results = {
          get spawnPoint() { return buildResults.spawnPoint ?? undefined; },
          set spawnPoint(v: Vector3 | undefined) { buildResults.spawnPoint = v ?? null; },
          get mapConfig() { 
            if (!buildResults.mapConfig) return undefined;
            // Convert MapConfig to NodeProcessResults.mapConfig format
            const gravity = typeof buildResults.mapConfig.physics?.gravity === 'number' 
              ? buildResults.mapConfig.physics.gravity 
              : -30;
            return {
              physics: { gravity },
              ambient: buildResults.mapConfig.ambient ?? new Vector3(0.2, 0.2, 0.2),
              maxLights: buildResults.mapConfig.maxLights,
              startCamera: buildResults.mapConfig.startCamera,
              fog: buildResults.mapConfig.fog,
              lens: buildResults.mapConfig.lens,
            };
          },
          set mapConfig(v: { physics: { gravity: number }; ambient: Vector3; maxLights?: number; startCamera?: string; fog?: FogConfig; lens?: LensConfig } | undefined) { 
            if (v) {
              // Merge with existing or set new - preserve all properties including fog and lens
              buildResults.mapConfig = { 
                ...buildResults.mapConfig, 
                physics: { gravity: v.physics.gravity },
                ambient: v.ambient,
                maxLights: v.maxLights,
                startCamera: v.startCamera,
                fog: v.fog,
                lens: v.lens,
              };
            } else {
              buildResults.mapConfig = null;
            }
          },
          get cullDistance() { return buildResults.cullDistance; },
          set cullDistance(v: number) { buildResults.cullDistance = v; },
          npcSpawns: buildResults.npcSpawns,
        };
        
        const ctx = {
          mesh,
          extras,
          scene: this.scene,
          services: {
            physics: this.physicsManager,
            triggers: this.triggerSystem,
            environment: this.environmentManager,
            particles: null as ParticleManager | null,
          },
          results,
          getParam: <T>(name: string, defaultValue?: T): T => {
            // Look for hm_behavior_{name} property
            const key = `${ENTITY_PROPERTY_PREFIX}_behavior_${name}`;
            const value = (extras as Record<string, unknown>)[key];
            return (value !== undefined ? value : defaultValue) as T;
          },
        };
        
        try {
          const result = behavior.onProcessNode(ctx);
          
          if (result.hideNode) {
            mesh.isVisible = false;
          }
          if (result.skipPhysics) {
            mesh.checkCollisions = false;
          }
          
          if (result.handled) {
            logger.level.log(`✅ Behavior '${behaviorId}' handled node: ${mesh.name}, mapConfig=${JSON.stringify(buildResults.mapConfig)}, cullDistance=${buildResults.cullDistance}`);
            return { handled: true, skipPhysics: result.skipPhysics ?? false };
          }
        } catch (error) {
          logger.level.error(`❌ Error in behavior '${behaviorId}' processing ${mesh.name}:`, error);
          throw error;
        }
      }
      
      return { handled: false, skipPhysics: false };
    }

    /**
     * Register glTF lights with UnifiedLightManager for gating (enable/disable).
     * 
     * Babylon.js glTF loader creates lights at origin (0,0,0) but the actual positions
     * are stored in TransformNodes with matching names. This method:
     * 1. Finds the correct position from TransformNodes
     * 2. Moves the light to the correct position
     * 3. Registers the light with UnifiedLightManager (no disposal, no property copying)
     */
    private registerMapLightsWithManager(_result: { transformNodes: TransformNode[] }): void {
        // Get all positional lights (point and spot) - exclude our pool lights and combat lights
        const mapLights = this.scene.lights.filter(l => 
            !l.name.startsWith('combat_light_') &&
            !l.name.startsWith('pool_light_') &&
            (l instanceof PointLight || l instanceof SpotLight)
        );
        
        if (mapLights.length === 0) {
            logger.level.debug('💡 No map lights to register');
            return;
        }
        
        let registeredCount = 0;
        
        for (const light of mapLights) {
            const posLight = light as PointLight | SpotLight;
            const isSpotLight = light instanceof SpotLight;
            
            // Get world position - glTF lights are parented to TransformNodes
            // The light itself is at local (0,0,0), position comes from the parent TransformNode chain
            let position = posLight.position.clone();
            let direction: Vector3 | undefined;
            
            // Get world position from parent TransformNode
            if (light.parent instanceof TransformNode) {
                const parentNode = light.parent as TransformNode;
                parentNode.computeWorldMatrix(true);
                position = parentNode.getAbsolutePosition().clone();
                
                // For spotlights, get world direction from parent rotation
                if (isSpotLight) {
                    const spotLight = light as SpotLight;
                    // Get the world rotation and transform the local direction
                    const worldMatrix = parentNode.getWorldMatrix();
                    direction = Vector3.TransformNormal(spotLight.direction, worldMatrix).normalize();
                }
            } else {
                // Fallback: look for a TransformNode with the same name as the light
                const matchingNode = this.scene.getTransformNodeByName(light.name);
                if (matchingNode) {
                    matchingNode.computeWorldMatrix(true);
                    position = matchingNode.getAbsolutePosition().clone();
                    
                    if (isSpotLight) {
                        const spotLight = light as SpotLight;
                        const worldMatrix = matchingNode.getWorldMatrix();
                        direction = Vector3.TransformNormal(spotLight.direction, worldMatrix).normalize();
                    }
                } else if (isSpotLight) {
                    // Use local direction as-is
                    direction = (light as SpotLight).direction.clone();
                }
            }
            
            // Get light properties BEFORE disposing
            const color = posLight.diffuse.clone();
            
            // Blender exports intensity in watts via KHR_lights_punctual
            // Babylon.js expects normalized values (typically 0.1-10)
            // Blender 158W → glTF ~27922 → we need ~1-5 for Babylon
            // Scale factor: divide by ~10000 to get reasonable values
            const rawIntensity = posLight.intensity;
            const intensity = rawIntensity / 10000; // Normalize from watts
            
            let range = posLight.range;
            
            // Set a reasonable range if it's infinite (default from glTF)
            if (range > 1000) {
                range = 15;
            }
            
            // Get SpotLight-specific properties
            let angle: number | undefined;
            let exponent: number | undefined;
            if (isSpotLight) {
                const spotLight = light as SpotLight;
                angle = spotLight.angle;
                exponent = spotLight.exponent;
            }
            
            // Register as a request with UnifiedLightManager (pool lights will be used)
            UnifiedLightManager.registerMapLight({
                position,
                color,
                intensity,
                range,
                lightType: isSpotLight ? 'spot' : 'point',
                direction,
                angle,
                exponent
            });
            
            registeredCount++;
            const typeStr = isSpotLight ? 'SPOT' : 'POINT';
            logger.level.debug(`💡 Registered ${typeStr} light: ${light.name} → pos:(${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}) intensity:${intensity.toFixed(2)} (raw: ${rawIntensity.toFixed(0)}) range:${range.toFixed(1)}${isSpotLight ? ` angle:${(angle! * 180 / Math.PI).toFixed(1)}°` : ''}`);
            
            // DISPOSE the original glTF light - we use pool lights instead
            light.dispose();
        }
        
        // Finalize - trigger pool assignment so lights actually illuminate the scene
        UnifiedLightManager.finalizeMapLights();
        
        logger.level.log(`💡 Registered ${registeredCount} map lights as requests (originals disposed, using pool)`);
    }

  public async build(config: LevelConfig): Promise<LevelData> {
    // Create light
    if (config.createDefaultLight !== false) {
        this.defaultLight = new HemisphericLight('light', new Vector3(0, 1, 0), this.scene);
        this.defaultLight.intensity = 0.1;
    }

    let loadedSpline: Curve3 | null = null;
    let spawnPoint: Vector3 | null = null;
    let splineSource: 'mesh' | 'data' | 'none' = 'none';
    let mapConfig: MapConfig = {};
    let cameraAnimations: import('@babylonjs/core').AnimationGroup[] = [];
    const pendingTriggers: Mesh[] = [];
    const npcSpawns: NPCMarkerData[] = [];
    let cullDistance = 30.0; // Default cull distance in meters
    let maxLights = 8; // Default max simultaneous lights

    // Load map
    try {
        logger.level.log(`Loading map from ${config.mapPath}...`);
        // Split path into root and filename
        const lastSlash = config.mapPath.lastIndexOf('/');
        const rootUrl = lastSlash >= 0 ? config.mapPath.substring(0, lastSlash + 1) : '';
        const fileName = lastSlash >= 0 ? config.mapPath.substring(lastSlash + 1) : config.mapPath;

        const result = await SceneLoader.ImportMeshAsync('', rootUrl, fileName, this.scene);
        
        // Track loaded resources for cleanup on level unload
        this.loadedMeshes = [...result.meshes];
        this.loadedTransformNodes = [...(result.transformNodes || [])];
        this.loadedAnimationGroups = [...(result.animationGroups || [])];
        
        // Extract camera animations from map for cinematics
        if (result.animationGroups && result.animationGroups.length > 0) {
            for (const animGroup of result.animationGroups) {
                // Check if any targeted animation targets a camera
                // Use BabylonJS class names only - no name-based detection
                const hasCameraTarget = animGroup.targetedAnimations.some((ta) => {
                    const target = ta.target;
                    const className = target?.getClassName?.();
                    return (
                        className === 'FreeCamera' ||
                        className === 'UniversalCamera' ||
                        className === 'ArcRotateCamera' ||
                        className === 'TargetCamera' ||
                        className === 'Camera'
                    );
                });
                
                if (hasCameraTarget) {
                    // Stop and reset the animation immediately to prevent auto-play
                    animGroup.stop();
                    animGroup.reset();
                    cameraAnimations.push(animGroup);
                    logger.level.log(`🎬 Found camera animation: "${animGroup.name}" (stopped for later playback)`);
                }
            }
        }
        
        // Apply scale to root
        if (result.meshes.length > 0) {
            // The first mesh is usually __root__
            // Flip X to fix mirroring issue (Blender -> Babylon coordinate difference)
            result.meshes[0].scaling = new Vector3(-config.mapScale, config.mapScale, config.mapScale);
        }

        // Video textures now handled by video_material behavior - no legacy scan

        // Initialize UnifiedLightManager BEFORE registering map lights
        if (!UnifiedLightManager.isInitialized()) {
            UnifiedLightManager.initialize(this.scene, maxLights);
        }

        // Register map lights with UnifiedLightManager for gating (enable/disable)
        // Lights are kept as-is, just registered for priority-based enable/disable
        this.registerMapLightsWithManager(result);

        // Note: maxSimultaneousLights is set later in optimizeMaterialsForDynamicLights
        // after we know the maxLights value from scene config

        logger.level.log(`📎 Map loaded with ${result.meshes.length} nodes. Scanning for paths...`);
        
        // Process TransformNodes (Blender EMPTY objects) that may contain asset roots
        if (result.transformNodes && result.transformNodes.length > 0) {
            logger.level.log(`📎 Found ${result.transformNodes.length} transform nodes, checking for asset roots...`);
            for (const node of result.transformNodes) {
                const extras = this.getExtras(node);
                if (!extras) continue;
                
                // Check for behavior ID - NEW: from hm_behaviors (asset:xyz format) or LEGACY: hm_behavior_id
                const behaviorsStr = getEntityProperty<string>(extras, 'behaviors', '');
                let behaviorId = '';
                
                // Parse behaviors and extract asset behavior if present
                const behaviorIds = behaviorsStr.split(',').map(b => b.trim()).filter(Boolean);
                for (const bid of behaviorIds) {
                    // Asset roots use asset:*, object:*, or other behaviors that need physics setup
                    if (bid.startsWith('asset:') || bid.startsWith('object:')) {
                        behaviorId = bid;
                        break;
                    }
                }
                
                
                const hasSpawnNpc = behaviorIds.includes('spawn:npc');
                
                // Handle NPC marker entities (placed as Empty in Blender)
                if (hasSpawnNpc) {
                    logger.level.log(`👤 Found NPC marker (TransformNode): ${node.name}`);
                    node.computeWorldMatrix(true);
                    
                    // Extract NPC spawn parameters (using hm_behavior_ prefix)
                    const characterId = getBehaviorParam<string>(extras, 'character_id', '');
                    const spawnRadius = getBehaviorParam<number>(extras, 'spawn_radius', 0);
                    const patrolLeftDistance = getBehaviorParam<number>(extras, 'patrol_left_distance', 3);
                    const patrolRightDistance = getBehaviorParam<number>(extras, 'patrol_right_distance', 3);
                    const patrolWaitTime = getBehaviorParam<number>(extras, 'patrol_wait_time', 2);
                    const maxHealth = getBehaviorParam<number>(extras, 'max_health', 0); // 0 = use character default
                    const startHealth = getBehaviorParam<number>(extras, 'start_health', 0); // 0 = use maxHealth
                    
                    // Extract behavior config (hm_behavior_npc_* annotations)
                    const npcBehaviorId = getBehaviorParam<string>(extras, 'npc_behavior_id', '');
                    const npcMovement = getBehaviorParam<string>(extras, 'npc_movement', '');
                    const npcCombat = getBehaviorParam<string>(extras, 'npc_combat', '');
                    const attackRange = getBehaviorParam<number>(extras, 'npc_attack_range', 0);
                    const detectionRange = getBehaviorParam<number>(extras, 'npc_detection_range', 0);
                    const attackCooldown = getBehaviorParam<number>(extras, 'npc_attack_cooldown', 0);
                    
                    if (!characterId) {
                        throw new Error(
                            `LevelBuilder: NPC marker '${node.name}' missing required property 'hm_behavior_character_id'. ` +
                            `Add this property in Blender to specify which character to spawn.`
                        );
                    }
                    const spawnData: NPCMarkerData = {
                        // IMPORTANT: Keep full node name including Blender suffix (.001, .002, etc.)
                        // to ensure unique IDs when duplicating spawn markers in Blender
                        id: node.name,
                        characterId,
                        position: node.getAbsolutePosition().clone(),
                        spawnRadius,
                        patrolLeftDistance,
                        patrolRightDistance,
                        patrolWaitTime,
                        maxHealth: maxHealth > 0 ? maxHealth : undefined, // Only set if specified
                        startHealth: startHealth > 0 ? startHealth : undefined, // Only set if specified
                        behaviorConfig: {
                            behaviorId: npcBehaviorId || undefined,
                            movement: (npcMovement as 'patrol' | 'idle' | 'follow' | 'wander') || undefined,
                            combat: (npcCombat as 'passive' | 'aggressive' | 'defensive' | 'territorial') || undefined,
                            attackRange: attackRange || undefined,
                            detectionRange: detectionRange || undefined,
                            attackCooldown: attackCooldown || undefined,
                            // Pass patrol distances for behavior resolution
                            patrolLeft: patrolLeftDistance,
                            patrolRight: patrolRightDistance,
                        },
                    };
                    npcSpawns.push(spawnData);
                    const behaviorInfo = npcBehaviorId || `${npcMovement || 'patrol'}/${npcCombat || 'passive'}`;
                    logger.level.log(`   ✅ NPC spawn: ${characterId} at ${spawnData.position}, radius=${spawnRadius}m, patrol=[${patrolLeftDistance}m, ${patrolRightDistance}m], behavior=${behaviorInfo}`);
                    continue;
                }
                
                if (behaviorId) {
                    logger.level.log(`📦 Found asset root (TransformNode): ${node.name} (behavior_id=${behaviorId})`, extras);
                    
                    // Lookup behavior via BehaviorRegistry (throws if not found - fail fast!)
                    const { behavior, resolvedId } = BehaviorRegistry.get(behaviorId, node.name);
                    logger.level.log(`   ✅ Using registered behavior: '${resolvedId}'`);
                    
                    // Get physics properties from asset root
                    const mass = getEntityProperty(extras, 'physics_mass', 10.0);
                    const friction = getEntityProperty(extras, 'physics_friction', 0.6);
                    const restitution = getEntityProperty(extras, 'physics_restitution', 0.2);
                    
                    node.computeWorldMatrix(true);
                    const position = node.getAbsolutePosition().clone();
                    
                    // Create unique object ID using BabylonJS uniqueId to handle duplicate instances
                    const objectId = `asset_${node.name}_${node.uniqueId}`;
                    logger.level.log(`   🆔 Object ID: ${objectId}`);
                    
                    // Find child meshes by hm_mesh_role
                    // Use getChildMeshes(true) to get ALL descendants, not just direct children
                    const childMeshes = node.getChildMeshes(true);
                    logger.level.log(`   🔍 Found ${childMeshes.length} child meshes under ${node.name}:`, childMeshes.map(m => m.name));
                    let renderMesh: AbstractMesh | null = null;
                    let collisionMesh: AbstractMesh | null = null;
                    const debrisMeshes: AbstractMesh[] = [];
                    // Track TransformNodes with render role (multi-material meshes export as TransformNode with child primitives)
                    let renderTransformNode: TransformNode | null = null;
                    
                    // First pass: check direct TransformNode children for render role
                    // Multi-material meshes in Blender export as TransformNode with hm_mesh_role, with child primitive meshes
                    for (const child of node.getChildren(undefined, false)) {
                        if (child instanceof TransformNode && !(child instanceof AbstractMesh)) {
                            const childExtras = this.getExtras(child);
                            const childRole = childExtras?.hm_mesh_role as string | undefined;
                            if (childRole === 'render') {
                                renderTransformNode = child;
                                logger.level.log(`   🎨 Found render TransformNode (multi-material): ${child.name}`);
                            }
                        }
                    }
                    
                    for (const childMesh of childMeshes) {
                        const meshExtras = this.getExtras(childMesh);
                        const meshRole = meshExtras?.hm_mesh_role as string | undefined;
                        
                        // Mark this mesh as processed as part of an asset
                        // This prevents it from being processed again in the main mesh loop
                        if (!childMesh.metadata) childMesh.metadata = {};
                        childMesh.metadata._processedAsAssetChild = true;
                        
                        // Check if this mesh's parent is a render TransformNode (multi-material mesh case)
                        const isChildOfRenderNode = renderTransformNode && childMesh.parent === renderTransformNode;
                        
                        if (meshRole === 'render' || isChildOfRenderNode) {
                            // For multi-material meshes, use the TransformNode parent as render "mesh"
                            // Store the first primitive for physics fallback, but prefer storing the TransformNode
                            if (isChildOfRenderNode && !renderMesh) {
                                // Store the TransformNode as render mesh reference for hierarchy management
                                // The actual primitive meshes will follow their parent
                                renderMesh = childMesh; // Use first primitive for physics fallback if needed
                                logger.level.log(`   🎨 Found render mesh (primitive of ${renderTransformNode!.name}): ${childMesh.name}`);
                            } else if (meshRole === 'render') {
                                renderMesh = childMesh;
                                logger.level.log(`   🎨 Found render mesh: ${childMesh.name}`);
                            }
                        } else if (meshRole === 'collision') {
                            collisionMesh = childMesh;
                            logger.level.log(`   🧱 Found collision mesh: ${childMesh.name}`);
                        } else if (meshRole === 'debris') {
                            debrisMeshes.push(childMesh);
                            logger.level.log(`   💥 Found debris mesh: ${childMesh.name}`);
                        }
                    }
                    
                    // Use collision mesh for physics, fall back to render mesh if no collision mesh
                    let physicsMesh: AbstractMesh | null = collisionMesh ?? renderMesh;
                    
                    if (!physicsMesh) {
                        logger.level.warn(`   ⚠️ No physics or render mesh found for ${objectId}, skipping physics`);
                        continue;
                    }
                    
                    logger.level.log(`   📍 Position: ${position.toString()}`);
                    
                    // CRITICAL: Babylon physics doesn't work well with parented meshes.
                    // We need to unparent the physics mesh and center its geometry for proper physics.
                    
                    // Get world transform using utility (works correctly for both Mesh and InstancedMesh)
                    const { position: worldPosition, scale: worldScaling } = getWorldTransform(physicsMesh);
                    const worldRotation = physicsMesh.absoluteRotationQuaternion?.toEulerAngles().clone() ?? physicsMesh.rotation.clone();
                    
                    logger.level.log(`   🌍 Physics mesh world pos: ${worldPosition.toString()}, scale: ${worldScaling.toString()}`);
                    
                    // Unparent physics mesh (moves to scene root)
                    physicsMesh.setParent(null);
                    
                    // Apply world transform
                    physicsMesh.position = worldPosition;
                    physicsMesh.rotation = worldRotation;
                    physicsMesh.scaling = worldScaling;
                    
                    // Force refresh bounding info to get correct world bounding box
                    physicsMesh.refreshBoundingInfo({ applySkeleton: true });
                    physicsMesh.computeWorldMatrix(true);
                    
                    // Get the bounding box center in local/mesh coordinates
                    let bbCenterLocal = physicsMesh.getBoundingInfo().boundingBox.center.clone();
                    
                    logger.level.log(`   🔧 BB center local: ${bbCenterLocal.toString()}`);
                    
                    // CRITICAL FIX: For InstancedMesh, we need to convert to a regular Mesh for physics.
                    // InstancedMesh shares geometry with source, so we can't modify its vertices.
                    // We clone the source mesh and position it at the instance's world position.
                    if (physicsMesh instanceof InstancedMesh) {
                        const sourceMesh = physicsMesh.sourceMesh;
                        logger.level.log(`   🔄 Converting InstancedMesh to Mesh for physics (source: ${sourceMesh.name})`);
                        
                        // CRITICAL: The source mesh may have already been vertex-centered.
                        // If so, its vertices are at origin and its position was adjusted.
                        // We need to compute: where should the GEOMETRY CENTER of the instance be?
                        
                        // Get the source mesh's current BB center (should be ~0 if already centered)
                        sourceMesh.refreshBoundingInfo({ applySkeleton: true });
                        const sourceBBCenter = sourceMesh.getBoundingInfo().boundingBox.center.clone();
                        const sourceAlreadyCentered = sourceBBCenter.length() < 0.001;
                        
                        // CRITICAL FIX: If source was vertex-centered, we need its ORIGINAL bbCenterLocal
                        // to correctly compute where the instance's geometry center should be.
                        // The instance's mesh origin + original offset = instance's geometry center
                        const originalBBCenterLocal: Vector3 = (sourceMesh as any)._originalBBCenterLocal ?? Vector3.Zero();
                        
                        logger.level.log(`      Source mesh BB center: ${sourceBBCenter.toString()}, already centered: ${sourceAlreadyCentered}`);
                        logger.level.log(`      Source original BB center local: ${originalBBCenterLocal.toString()}`);
                        
                        // CRITICAL FIX: The instance's worldPosition is already correct!
                        // It was computed from the instance's local position + parent transform.
                        // The source mesh's geometry is now centered (vertices at origin), so when we clone it,
                        // we just position the clone at worldPosition directly.
                        // 
                        // Previous bug: adding originalBBCenterLocal offset was WRONG because:
                        // - worldPosition already accounts for the instance's placement in world space
                        // - originalBBCenterLocal is the offset that was applied to CENTER the source geometry
                        // - Adding it again would "double-offset" the instance
                        const instanceGeometryCenterWorld = worldPosition.clone();
                        
                        const sourceWorldPos = getWorldPosition(sourceMesh);
                        const instanceOffset = instanceGeometryCenterWorld.subtract(sourceWorldPos);
                        
                        logger.level.log(`      Source world pos: ${sourceWorldPos.toString()}`);
                        logger.level.log(`      Instance mesh origin world pos: ${worldPosition.toString()}`);
                        logger.level.log(`      Instance geometry center world (FIXED): ${instanceGeometryCenterWorld.toString()}`);
                        logger.level.log(`      Instance offset from source geometry center: ${instanceOffset.toString()}`);
                        
                        // Clone the source mesh geometry
                        // CRITICAL: Pass clonePhysicsImpostor=false to prevent cloning the source's impostor!
                        // Otherwise the clone gets a copy of the source's physics body, causing conflicts.
                        const clonedMesh = sourceMesh.clone(`${physicsMesh.name}_converted`, null, true, false);
                        if (!clonedMesh) {
                            logger.level.error(`   ❌ Failed to clone InstancedMesh source`);
                            continue;
                        }
                        
                        // CRITICAL: Reset metadata to avoid sharing with source mesh!
                        // BabylonJS clone() shares metadata by reference, which causes issues with
                        // _debrisMeshes and _renderMesh being shared between source and cloned meshes.
                        clonedMesh.metadata = {};
                        
                        // CRITICAL: Make geometry unique so we have our own copy of vertices!
                        clonedMesh.makeGeometryUnique();
                        
                        // Position the clone at the instance's geometry center
                        // Since source was vertex-centered, clone geometry is also centered, so position = geometry center
                        const clonePosition = instanceGeometryCenterWorld;
                        clonedMesh.position.copyFrom(clonePosition);
                        clonedMesh.rotation.copyFrom(worldRotation);
                        clonedMesh.scaling.copyFrom(worldScaling);
                        clonedMesh.isVisible = false;
                        clonedMesh.isPickable = false;
                        
                        logger.level.log(`      Clone position: ${clonePosition.toString()}`);
                        
                        clonedMesh.refreshBoundingInfo({ applySkeleton: true });
                        clonedMesh.computeWorldMatrix(true);
                        
                        // Update bbCenterLocal for the cloned mesh (should be ~0 if source was centered)
                        bbCenterLocal = clonedMesh.getBoundingInfo().boundingBox.center.clone();
                        
                        // Dispose the InstancedMesh and replace physicsMesh reference
                        physicsMesh.dispose();
                        physicsMesh = clonedMesh;
                        
                        // Also update collisionMesh if it was the same as physicsMesh
                        if (collisionMesh instanceof InstancedMesh && collisionMesh.name === physicsMesh.name) {
                            collisionMesh = clonedMesh;
                        }
                        
                        logger.level.log(`   ✅ Converted to Mesh: ${clonedMesh.name}, pos: ${clonedMesh.position.toString()}, bbCenter: ${bbCenterLocal.toString()}`);
                    }
                    
                    // CRITICAL FIX: Shift vertex data so geometry is centered at mesh origin.
                    // This ensures mesh.position = BB center, so PhysicsImpostor._deltaPosition = 0.
                    // After centering vertices, we move the mesh to keep geometry in place.
                    if (physicsMesh instanceof Mesh && (bbCenterLocal.x !== 0 || bbCenterLocal.y !== 0 || bbCenterLocal.z !== 0)) {
                        // CRITICAL: Make geometry unique before modifying vertices!
                        // This prevents corrupting geometry shared with other meshes/instances
                        physicsMesh.makeGeometryUnique();
                        
                        // CRITICAL: Store original bbCenterLocal on the mesh for InstancedMesh processing
                        // Instances processed later need to know how much the geometry was offset
                        (physicsMesh as any)._originalBBCenterLocal = bbCenterLocal.clone();
                        
                        // Get vertex positions
                        const positions = physicsMesh.getVerticesData(VertexBuffer.PositionKind);
                        if (positions) {
                            // Create new array for modified positions
                            const newPositions = new Float32Array(positions.length);
                            
                            // Shift all vertices by negative of local BB center to center geometry at mesh origin
                            for (let i = 0; i < positions.length; i += 3) {
                                newPositions[i] = positions[i] - bbCenterLocal.x;
                                newPositions[i + 1] = positions[i + 1] - bbCenterLocal.y;
                                newPositions[i + 2] = positions[i + 2] - bbCenterLocal.z;
                            }
                            
                            // Use setVerticesData with updatable=true (GLB buffers are non-updatable by default)
                            physicsMesh.setVerticesData(VertexBuffer.PositionKind, newPositions, true);
                            
                            // CRITICAL: Move mesh position by the same amount we shifted vertices (in world coords)
                            // This keeps the geometry in the same world position.
                            // Need to transform local offset by mesh scaling (assume no rotation for simplicity)
                            const offsetWorld = bbCenterLocal.multiply(physicsMesh.scaling);
                            physicsMesh.position.addInPlace(offsetWorld);
                            
                            // Refresh bounding info - now BB center should be at mesh origin (0,0,0 in local)
                            physicsMesh.refreshBoundingInfo({ applySkeleton: true });
                            physicsMesh.computeWorldMatrix(true);
                            
                            logger.level.log(`   🔧 After vertex centering: Mesh pos: ${physicsMesh.position.toString()}, BB center local: ${physicsMesh.getBoundingInfo().boundingBox.center.toString()}`);
                        }
                    }
                    
                    logger.level.log(`   🔧 Centered geometry. Mesh position: ${physicsMesh.position.toString()}`);
                    
                    // Hide collision mesh (should only be used for physics, not rendered)
                    if (collisionMesh) {
                        collisionMesh.isVisible = false;
                        collisionMesh.isPickable = false;
                        logger.level.log(`   👁️ Collision mesh hidden: ${collisionMesh.name}`);
                    }
                    
                    // If we have both render and collision meshes, parent render to collision
                    // so render follows physics movement
                    if (renderMesh && collisionMesh && renderMesh !== collisionMesh) {
                        // Get world transform using utility (works correctly for both Mesh and InstancedMesh)
                        const { position: renderWorldPos, scale: renderWorldScale } = getWorldTransform(renderMesh);
                        const renderWorldRot = renderMesh.absoluteRotationQuaternion?.toEulerAngles().clone() ?? renderMesh.rotation.clone();
                        
                        renderMesh.setParent(null);
                        renderMesh.position = renderWorldPos;
                        renderMesh.rotation = renderWorldRot;
                        renderMesh.scaling = renderWorldScale;
                        
                        // Apply the same vertex centering to render mesh as we did to physics mesh
                        // After centering vertices, move mesh to keep geometry in place
                        renderMesh.refreshBoundingInfo({ applySkeleton: true });
                        const renderBBCenterLocal = renderMesh.getBoundingInfo().boundingBox.center.clone();
                        
                        if (renderMesh instanceof Mesh && (renderBBCenterLocal.x !== 0 || renderBBCenterLocal.y !== 0 || renderBBCenterLocal.z !== 0)) {
                            const positions = renderMesh.getVerticesData('position');
                            if (positions) {
                                for (let i = 0; i < positions.length; i += 3) {
                                    positions[i] -= renderBBCenterLocal.x;
                                    positions[i + 1] -= renderBBCenterLocal.y;
                                    positions[i + 2] -= renderBBCenterLocal.z;
                                }
                                renderMesh.setVerticesData('position', positions);
                                
                                // CRITICAL: Move mesh position by the same amount we shifted vertices (in world coords)
                                const offsetWorld = renderBBCenterLocal.multiply(renderMesh.scaling);
                                renderMesh.position.addInPlace(offsetWorld);
                                
                                renderMesh.refreshBoundingInfo({ applySkeleton: true });
                            }
                        }
                        
                        // Capture the render mesh world position BEFORE parenting
                        const renderFinalWorldPos = getWorldPosition(renderMesh);
                        
                        // Now parent render mesh to physics mesh
                        const physicsWorldPos = getWorldPosition(physicsMesh);
                        
                        renderMesh.setParent(physicsMesh);
                        
                        // CRITICAL FIX: For InstancedMesh render meshes, we need to account for the 
                        // geometry center offset, just like we did for the physics mesh.
                        // After vertex centering, the physics mesh is at the geometry center.
                        // The render mesh should also be centered at the same geometry center.
                        // Since both share the same parent empty and local offset pattern in Blender,
                        // the render mesh's geometry center should be at the same world position as physics mesh.
                        // Therefore, the local position should be (0,0,0).
                        if (renderMesh instanceof InstancedMesh) {
                            // For InstancedMesh, after the physics mesh has been vertex-centered and positioned
                            // at the geometry center, the render mesh should sit at the same position.
                            // Set local position to (0,0,0) since both meshes share the same geometry center.
                            renderMesh.position = Vector3.Zero();
                            logger.level.log(`      InstancedMesh render: local pos set to (0,0,0) to match physics geometry center`);
                        } else {
                            // Calculate local position relative to physics mesh using positions captured BEFORE parenting
                            renderMesh.position = renderFinalWorldPos.subtract(physicsWorldPos);
                        }
                        // Rotation and scale adjustments
                        renderMesh.rotation = renderWorldRot.subtract(physicsMesh.rotation);
                        renderMesh.scaling = renderWorldScale.divide(physicsMesh.scaling);
                        renderMesh.computeWorldMatrix(true);
                        
                        logger.level.log(`   🔗 Render mesh parented: local pos=${renderMesh.position.toString()}`);
                        logger.level.log(`   🔗 Render mesh parented to physics mesh`);
                    }
                    
                    // Parent debris meshes to physics mesh so they move with it and can be found on destruction
                    // CRITICAL: Convert InstancedMesh debris to regular Mesh during loading.
                    // This is essential because InstancedMesh visibility is shared with source mesh,
                    // so showing debris on one instance would show it on ALL instances!
                    physicsMesh.computeWorldMatrix(true);
                    
                    const preparedDebrisMeshes: AbstractMesh[] = [];
                    for (const debris of debrisMeshes) {
                        // Use prepareDebrisForSpawning which handles InstancedMesh -> Mesh conversion
                        // This converts at load time so destruction handling is simpler
                        const preparedDebris = prepareDebrisForSpawning(debris, physicsMesh);
                        if (preparedDebris) {
                            preparedDebrisMeshes.push(preparedDebris);
                            logger.level.log(`      Prepared debris: ${preparedDebris.name}`);
                        }
                    }
                    
                    // Use the converted meshes for storage
                    if (preparedDebrisMeshes.length > 0) {
                        logger.level.log(`   💥 Prepared ${preparedDebrisMeshes.length} debris meshes for physics mesh`);
                    }
                    // Replace debrisMeshes with converted ones for metadata storage
                    debrisMeshes.length = 0;
                    debrisMeshes.push(...preparedDebrisMeshes);
                    
                    // Store debris mesh references in physics mesh metadata for destruction handler
                    if (!physicsMesh.metadata) physicsMesh.metadata = {};
                    physicsMesh.metadata._debrisMeshes = debrisMeshes;
                    physicsMesh.metadata._renderMesh = renderMesh; // Store render mesh for destruction handler
                    // For multi-material meshes, also store the parent TransformNode
                    if (renderTransformNode) {
                        physicsMesh.metadata._renderTransformNode = renderTransformNode;
                        logger.level.log(`   🎨 Stored render TransformNode: ${renderTransformNode.name}`);
                    }
                    
                    // Enable physics on the collision mesh (now unparented, at root level)
                    logger.level.debug(`   🔧 Creating physics body for ${objectId} using ${physicsMesh.name}...`, { mass, friction, restitution });
                    const body = this.physicsManager.createBody(
                        objectId,
                        physicsMesh,
                        PhysicsShapeType.BOX,
                        {
                            mass,
                            friction,
                            restitution,
                            collisionGroup: CollisionGroups.DYNAMIC,
                            collisionMask: CollisionMasks.DYNAMIC,
                        }
                    );
                    
                    logger.level.log(`   ✅ Physics body created: ${body ? 'SUCCESS' : 'FAILED'}`, body);
                    
                    // Create XState actor and register with StateManager
                    if (behavior.createMachine && this.stateManager) {
                        logger.level.debug(`   🎭 Creating XState actor for ${objectId}...`);
                        const machine = behavior.createMachine();
                        const actor = createActor(machine, {
                            input: {
                                id: objectId,
                                assetId: resolvedId,
                                behaviorId: resolvedId,
                                totalDamage: 0,
                                breakThreshold: 100,
                                debrisLifetime: 5,
                                health: 10,
                                maxHealth: 10,
                            }
                        });
                        actor.start();
                        this.stateManager.registerDynamicObject(objectId, actor);
                        
                        // Subscribe to state changes to handle destroyed state
                        actor.subscribe((state) => {
                            logger.assets.log(`[${objectId}] State changed to: ${state.value}`);
                            if (state.value === 'destroyed') {
                                this.handleObjectDestroyed(objectId, physicsMesh);
                            }
                        });
                        
                        // Register the physics mesh for combat damage detection
                        // CRITICAL: For InstancedMesh assets, physicsMesh is now the converted Mesh (_converted suffix)
                        // We must register THIS mesh, not the original hitMesh, because:
                        // 1. The physics body is on physicsMesh (has physicsImpostor)
                        // 2. Raycasts in combat use physics, so they hit physicsMesh
                        // 3. The original InstancedMesh is disposed after conversion
                        if (physicsMesh) {
                            this.stateManager.registerDamageableMesh(physicsMesh, objectId);
                            logger.level.log(`   🎯 Registered hit mesh: ${physicsMesh.name} -> ${objectId}`);
                        }
                        
                        logger.level.log(`   🎭 XState actor registered: ${objectId}`, actor.getSnapshot().value);
                        
                        // Register with DynamicObjectSystem for collision hooks
                        // Pass the actor so DynamicObjectSystem uses the same instance
                        if (this.dynamicObjectSystem) {
                            this.dynamicObjectSystem.registerMapAsset(
                                objectId,
                                objectId, // physicsBodyId equals objectId for LevelBuilder assets
                                resolvedId,
                                physicsMesh,
                                actor as any // Pass the actor we just created
                            );
                        }
                    } else {
                        logger.level.warn(`   ⚠️ Could not create XState actor: behavior.createMachine=${!!behavior.createMachine}, stateManager=${!!this.stateManager}`);
                        
                        // Still register with DynamicObjectSystem even without actor
                        if (this.dynamicObjectSystem) {
                            this.dynamicObjectSystem.registerMapAsset(
                                objectId,
                                objectId,
                                resolvedId,
                                physicsMesh
                            );
                        }
                    }
                    
                    // Note: onSpawn is now called by DynamicObjectSystem.registerMapAsset()
                }
            }
        }
        
        // Process Lights
        if (result.lights && result.lights.length > 0) {
            logger.level.debug(`💡 Found ${result.lights.length} lights in map`);
            result.lights.forEach(light => {
                logger.level.debug(`   - ${light.name} [${light.getClassName()}] Intensity: ${light.intensity}`);
                
                // Enable shadows for Directional and Spot lights
                // Point lights are expensive for shadows, so maybe skip or make optional
                if (light instanceof DirectionalLight || light instanceof SpotLight) {
                    const shadowGenerator = new ShadowGenerator(1024, light);
                    shadowGenerator.useBlurExponentialShadowMap = true;
                    shadowGenerator.blurKernel = 32;
                    
                    // Enable transparent shadows (respects material alpha)
                    shadowGenerator.transparencyShadow = true;
                    shadowGenerator.enableSoftTransparentShadow = true;
                    
                    // CRITICAL: Configure filter to exclude debug meshes by layer mask
                    // This works for meshes created AFTER the shadow generator too
                    configureDebugMeshFilter(shadowGenerator);
                    
                    // Add all meshes to shadow caster list (except gameplay paths and debug meshes)
                    result.meshes.forEach(m => {
                        if (m.isVisible && m.name !== '__root__') {
                            // Skip gameplay path meshes - they shouldn't cast shadows
                            const meshExtras = this.getExtras(m);
                            const isGameplayPath = meshExtras?.hm_gameplay_path === true;
                            // Skip debug meshes (layer mask check is also in filterMesh for runtime)
                            const isDebug = m.metadata?.isDebug === true || m.metadata?.noShadows === true;
                            
                            if (!isGameplayPath && !isDebug) {
                                shadowGenerator.addShadowCaster(m as Mesh, true);
                                m.receiveShadows = true;
                            }
                        }
                    });
                    
                    logger.level.debug(`      + Shadows enabled`);
                }
                
                // Adjust intensity if needed (Blender exports can be very bright)
                light.intensity *= 0.001;
            });
        }

        result.meshes.forEach(m => {
            if (m.name !== '__root__') {
                const meshExtras = this.getExtras(m);
                const isGameplayPath = meshExtras?.hm_gameplay_path === true;
                const vertexCount = m instanceof Mesh ? m.getTotalVertices() : 0;
                const parentName = m.parent ? m.parent.name : 'NONE';
                logger.level.debug(` - ${m.name} [${m.getClassName()}] (Vertices: ${vertexCount}, Parent: ${parentName})${isGameplayPath ? ' 📍 GAMEPLAY PATH' : ''}`);
            }
        });

        // Iterate over loaded meshes and create physics bodies
        for (const mesh of result.meshes) {
            // Skip the root node (__root__) if it has no geometry
            if (mesh.name === '__root__') continue;

            // Skip meshes that were already processed as part of an asset (hm_behavior_id)
            // These have been handled in the TransformNode processing above
            if (mesh.metadata?._processedAsAssetChild) {
                logger.level.debug(`⏭️ Skipping ${mesh.name} - already processed as asset child`);
                continue;
            }

            // Check if mesh should be hidden via custom property
            if (this.hasHiddenProperty(mesh)) {
                logger.level.debug(`🙈 Hidden via custom property: ${mesh.name} (invisible but collision active)`);
                mesh.isVisible = false;
                // Don't disable - keep collision/physics active, just hide rendering
            }

            // Check if this is a gameplay path (exported from Blender as LinesMesh)
            // ONLY detect paths via hm_gameplay_path property - NO name or topology detection
            if (mesh instanceof Mesh) {
                const meshExtras = this.getExtras(mesh);
                const isGameplayPath = meshExtras?.hm_gameplay_path === true;
                
                if (isGameplayPath) {
                    const positions = mesh.getVerticesData('position');
                    
                    if (positions) {
                        const vertexCount = positions.length / 3;
                        logger.level.log(`🎯 Found gameplay path: ${mesh.name} (${vertexCount} vertices)`);
                        mesh.computeWorldMatrix(true);
                        
                        // Extract vertices from the LinesMesh
                        const points: Vector3[] = [];
                        for (let i = 0; i < positions.length; i += 3) {
                            const localPoint = new Vector3(positions[i], positions[i + 1], positions[i + 2]);
                            const worldPoint = Vector3.TransformCoordinates(localPoint, mesh.getWorldMatrix());
                            points.push(worldPoint);
                        }
                        
                        if (points.length >= 8) {
                            // Only load the first valid spline found (don't overwrite)
                            if (!loadedSpline) {
                                loadedSpline = new Curve3(points);
                                splineSource = 'mesh';
                                logger.level.log(`✅ Loaded gameplay path with ${points.length} points`);
                            } else {
                                throw new Error(
                                    `Multiple gameplay paths detected in level! Found "${mesh.name}" (${points.length} points) but already loaded path with ${loadedSpline.getPoints().length} points. Only one object should have hm_gameplay_path=true.`
                                );
                            }
                            
                            // Hide the curve mesh
                            mesh.isVisible = false;
                            mesh.checkCollisions = false;
                            mesh.receiveShadows = false;
                            mesh.metadata = { ...(mesh.metadata || {}), isDebug: true, noShadows: true };
                            continue; // Skip physics for curve
                        } else {
                            throw new Error(
                                `Gameplay path "${mesh.name}" has only ${points.length} points. Minimum 8 points required.`
                            );
                        }
                    } else {
                        throw new Error(
                            `Gameplay path "${mesh.name}" has no vertex data. Export the curve as a mesh.`
                        );
                    }
                }
            }

            // Check for behaviors on mesh
            const extras = this.getExtras(mesh);
            
            // Debug: Log meshes with hm_behaviors
            if (extras && extras.hm_behaviors) {
              logger.level.log(`🎯 Found mesh with hm_behaviors: ${mesh.name} = "${extras.hm_behaviors}"`);
            }
            
            // Try processing via behavior system first (unified approach)
            const buildResults: {
              spawnPoint: Vector3 | null;
              mapConfig: MapConfig | null;
              cullDistance: number;
              npcSpawns: NPCMarkerData[];
            } = {
              spawnPoint,
              mapConfig,
              cullDistance,
              npcSpawns,
            };
            const behaviorResult = this.processNodeViaBehavior(mesh, extras ?? {}, buildResults);
            if (behaviorResult.handled) {
              // Update local vars from results
              spawnPoint = buildResults.spawnPoint;
              if (buildResults.mapConfig) {
                mapConfig = buildResults.mapConfig;
                // Read maxLights from mapConfig if set
                if (mapConfig.maxLights !== undefined) {
                  maxLights = mapConfig.maxLights;
                }
              }
              cullDistance = buildResults.cullDistance;
              continue; // Behavior handled this node
            }
            
            // All entity types are now handled via behavior system
            // spawn:player, config:scene, trigger:gravity_zone etc. are processed above

            // Skip meshes with material behaviors - they're handled separately and don't need physics
            const materialBehaviorIds = ['video_material', 'lava_material', 'particle_emitter'];
            const meshBehaviorId = getEntityProperty<string>(extras, 'behavior_id', '');
            if (meshBehaviorId && materialBehaviorIds.includes(meshBehaviorId)) {
                logger.level.debug(`⏭️ Skipping ${mesh.name} - material behavior (${meshBehaviorId}), no physics needed`);
                mesh.isVisible = true;
                mesh.checkCollisions = false;
                continue;
            }

            // --- Custom Collision Logic ---
            logger.level.debug(`[MESH INSPECTION] Processing mesh: "${mesh.name}"`);

            // 1. Check Blender custom property for collision
            const hasCollisionFlag = this.hasCollisionProperty(mesh);
            const isHidden = this.hasHiddenProperty(mesh);
            
            if (hasCollisionFlag) {
                logger.level.log(`🔶 Collision enabled on: ${mesh.name}`);
                logger.level.debug(`🔷 Collision enabled via custom property: ${mesh.name}`);
                // Respect hidden flag - don't override it
                if (!isHidden) {
                    mesh.isVisible = true;
                }
                mesh.checkCollisions = true;
                
                if (mesh.getTotalVertices() > 0) {
                    this.physicsManager.createStaticMesh(`collision_${mesh.name}`, mesh, 0.5);
                    logger.level.log(`   ✅ Created STATIC physics body for: ${mesh.name}${isHidden ? ' (hidden)' : ''}`);
                    logger.level.debug(`   ✅ Created physics body for: ${mesh.name}${isHidden ? ' (hidden)' : ''}`);
                }
                continue; // Skip default processing
            }

            // 2. Collider-Only Meshes (Invisible, Physics Enabled)
            // Uses hm_mesh_role property - NO name-based detection
            const meshRole = getEntityProperty<string>(extras, 'mesh_role', '');
            if (meshRole === 'collider') {
                logger.level.debug(`Found collider mesh (hm_mesh_role): ${mesh.name}`);
                mesh.isVisible = false; // Invisible
                mesh.checkCollisions = true;
                
                if (mesh.getTotalVertices() > 0) {
                    this.physicsManager.createStaticMesh(mesh.name, mesh, 0.5);
                }
                continue;
            }

            // 3. Visual-Only Meshes (Visible, Physics Disabled)
            // Uses hm_mesh_role property - NO name-based detection
            if (meshRole === 'visual') {
                logger.level.debug(`Found visual-only mesh (hm_mesh_role): ${mesh.name}`);
                mesh.isVisible = true;
                mesh.checkCollisions = false; // No physics
                continue;
            }

            // Check if this is a trigger entity (by behaviors ONLY - no naming convention)
            const behaviorsStr = getEntityProperty<string>(extras, 'behaviors', '');
            const isTriggerByBehavior = behaviorsStr.split(',').some(b => b.trim().startsWith('trigger:'));
            
            if (isTriggerByBehavior) {
                logger.level.log(`📷 Found trigger: ${mesh.name}`);
                pendingTriggers.push(mesh as Mesh);
                mesh.isVisible = false;
                mesh.checkCollisions = false;
                continue;
            }

            // Note: config:environment merged into config:scene - environment is processed via onProcessNode

            // God rays are now detected via hm_materialGodrays_enabled on materials
            // See PostProcessManager.detectAndInitializeGodRays()

            // Legacy particle entity type removed - use particle_emitter behavior instead
            // Particles are now spawned via behavior system with hm_behavior_id: 'particle_emitter'

            // Asset instance detection (hm_behavior_id property)
            const behaviorId = getEntityProperty<string>(extras, 'behavior_id', '');
            if (behaviorId) {
                logger.level.log(`🎯 Found asset instance: ${mesh.name} (behavior_id=${behaviorId})`);
                
                // Lookup behavior via BehaviorRegistry (throws if not found - fail fast!)
                const { behavior, resolvedId } = BehaviorRegistry.get(behaviorId, mesh.name);
                logger.level.log(`   ✅ Using registered behavior: '${resolvedId}'`);
                
                // Get physics properties from asset root
                const mass = getEntityProperty(extras, 'physics_mass', 10.0);
                const friction = getEntityProperty(extras, 'physics_friction', 0.6);
                const restitution = getEntityProperty(extras, 'physics_restitution', 0.2);
                
                mesh.computeWorldMatrix(true);
                const position = mesh.getAbsolutePosition().clone();
                const scale = mesh.scaling.clone();
                
                // Spawn XState actor for this asset
                // CRITICAL: Use BabylonJS uniqueId to handle duplicate instances
                const objectId = `asset_${mesh.name}_${mesh.uniqueId}`;
                logger.level.log(`   🆔 Object ID: ${objectId}`);
                const spawnedMesh: AbstractMesh = mesh;
                
                // If mesh has no children, it's a simple mesh - treat it directly as physics body
                if (!mesh.getChildren().length) {
                    logger.level.log(`   📦 Simple mesh (no children) - using directly for physics`);
                    logger.level.log(`   📍 Position: ${position.toString()}`);
                    logger.level.log(`   📏 Scale: ${scale.toString()}`);
                    
                    // Enable physics using PhysicsManager.createBody
                    logger.level.log(`   🔧 Creating physics body...`);
                    logger.level.log(`      objectId: ${objectId}`);
                    logger.level.log(`      shape: BOX`);
                    logger.level.log(`      mass: ${mass}`);
                    logger.level.log(`      collisionGroup: ${CollisionGroups.DYNAMIC} (DYNAMIC)`);
                    logger.level.log(`      collisionMask: ${CollisionMasks.DYNAMIC} (should include STATIC)`);
                    
                    const body = this.physicsManager.createBody(
                        objectId,
                        mesh,
                        PhysicsShapeType.BOX,
                        {
                            mass,
                            friction,
                            restitution,
                            collisionGroup: CollisionGroups.DYNAMIC,
                            collisionMask: CollisionMasks.DYNAMIC,  // Collides with STATIC, DYNAMIC, CHARACTER
                        }
                    );
                    
                    logger.level.log(`   ✅ Physics body created: ${body ? 'SUCCESS' : 'FAILED'}`);
                    if (body) {
                        logger.level.log(`      Physics impostor exists: ${!!mesh.physicsImpostor}`);
                        logger.level.log(`      Physics impostor type: ${mesh.physicsImpostor ? mesh.physicsImpostor.type : 'NONE'}`);
                    }
                }
                
                // Note: onSpawn is called by DynamicObjectSystem.registerMapAsset()
                
                // Create XState actor and register with StateManager for debug UI observability
                logger.level.log(`   🎭 Creating XState actor...`);
                logger.level.log(`      behavior.createMachine exists: ${!!behavior.createMachine}`);
                if (behavior.createMachine) {
                    const machine = behavior.createMachine();
                    const actor = createActor(machine, {
                        input: {
                            id: objectId,
                            totalDamage: 0,
                            breakThreshold: 100,
                            debrisLifetime: 5,
                            health: 10,
                            maxHealth: 10,
                        }
                    });
                    logger.level.log(`      Registering with StateManager as: ${objectId}`);
                    StateManager.getInstance().registerDynamicObject(objectId, actor as any);
                    actor.start();
                    logger.level.log(`   🔄 Started XState actor for ${objectId} (state: ${actor.getSnapshot().value})`);
                    logger.level.log(`      Actor ID in StateManager: ${objectId}`);
                    
                    // Subscribe to state changes to handle destroyed state
                    actor.subscribe((state) => {
                        logger.assets.log(`[${objectId}] State changed to: ${state.value}`);
                        if (state.value === 'destroyed') {
                            this.handleObjectDestroyed(objectId, spawnedMesh);
                        }
                    });
                } else {
                    logger.level.warn(`   ⚠️ No createMachine function for ${behaviorId}!`);
                }
                
                continue;
            }

            // 7. Gameplay paths are already handled above via hm_gameplay_path
            // No name-based detection - paths MUST have the property set

            // --- Default Behavior (Visible, No Physics) ---
            // Meshes without collision flag are visual only
            mesh.isVisible = true;
            mesh.isPickable = true;
            mesh.checkCollisions = false;
            
            // Physics bodies are only created for meshes with collision=true property or naming conventions
            logger.level.debug(`Loaded visual mesh (no collision): ${mesh.name}`);
        }
        
        logger.level.log('✅ Map loaded successfully');

        // Process material behaviors (video_material, lava_material, particle_emitter)
        // These are meshes with hm_behavior_id that don't need physics/state machines
        this.processMaterialBehaviors(result.meshes);

        // Detect and initialize god rays sources
        if (this.postProcessManager) {
            this.postProcessManager.detectAndInitializeGodRays();
        }

        if (!loadedSpline && splineSource === 'none') {
             logger.level.warn(`⚠️ No curve/path found in the map. Add a Blender curve named 'gameplay_path' or similar.`);
        }

        // Process pending triggers now that spline is loaded
        if (this.triggerSystem) {
            for (const mesh of pendingTriggers) {
                this.processTrigger(mesh);
            }
        }

        // IMPORTANT: Order matters here!
        // 1. UnifiedLightManager already initialized above (before map light conversion)
        // 2. Set cull distance on UnifiedLightManager
        // 3. Set maxSimultaneousLights on all materials  
        // 4. Warmup shaders with pool lights (they're always enabled with 0 intensity)
        // 5. Initialize spatial culling for meshes only (lights managed by UnifiedLightManager)
        
        logger.level.log(`💡 Light budget: ${maxLights} total, cull distance: ${cullDistance}m (managed by UnifiedLightManager)`);
        
        // Set light cull distance
        UnifiedLightManager.setCullDistance(cullDistance);
        
        // Pre-configure materials with total light budget
        this.optimizeMaterialsForDynamicLights(maxLights);
        
        // Force shader compilation and freeze materials
        // This ensures all materials compile while all meshes are still enabled
        await this.warmupShaders();

        // Initialize spatial culling system for MESHES ONLY
        // Lights are managed entirely by UnifiedLightManager
        this.initializeSpatialCulling(cullDistance, maxLights);

    } catch (error) {
        logger.userError('❌ Failed to load map:', error);
        // No fallbacks - throw on error!
        throw error;
    }

        const levelData: LevelData = { spline: loadedSpline, spawnPoint, splineSource, mapConfig, npcSpawns, cameraAnimations };
        this.lastLevelData = levelData;
        
        // Store spawn point in PhysicsManager for respawn functionality
        if (spawnPoint && this.physicsManager) {
            this.physicsManager.setSpawnPoint(spawnPoint);
        }
        
        return levelData;
  }

  private processTrigger(mesh: Mesh): void {
      if (!this.triggerSystem) {
          logger.level.warn(`⚠️ TriggerSystem not available for trigger: ${mesh.name}`);
          return;
      }

      // Use TriggerSystem's registerFromMesh which handles metadata parsing
      logger.level.log(`   📷 Registering trigger with TriggerSystem: ${mesh.name}`);
      try {
          this.triggerSystem.registerFromMesh(mesh);
          logger.level.log(`   ✅ Trigger registered: ${mesh.name}`);
      } catch (error) {
          logger.level.error(`   ❌ Error registering trigger ${mesh.name}:`, error);
      }
  }

  /**
   * Initialize the spatial culling system for distance-based object culling.
   * Registers all eligible meshes for culling based on player distance.
   * Lights are processed directly from scene.lights (no registration needed).
   */
  private initializeSpatialCulling(cullDistance: number, maxLights: number): void {
      // Create the spatial culling manager
      const cullingManager = new SpatialCullingManager(this.scene, cullDistance, maxLights);
      
      // Register with ServiceRegistry for global access
      ServiceRegistry.setSpatialCulling(cullingManager);
      
      let meshCount = 0;
      
      // Register meshes for culling
      for (const mesh of this.scene.meshes) {
          // Skip meshes that shouldn't be culled
          if (this.shouldSkipCulling(mesh)) {
              continue;
          }
          
          cullingManager.registerMesh(mesh);
          meshCount++;
      }
      
      // Count positional lights for logging (lights are processed directly, not registered)
      const lightCount = this.scene.lights.filter(
        l => l instanceof PointLight || l instanceof SpotLight
      ).length;
      
      logger.level.log(`📐 Spatial culling initialized: ${meshCount} meshes, ${lightCount} lights (max ${maxLights}), distance=${cullDistance}m`);
      
      // NOTE: Materials are already configured and frozen in warmupShaders()
      // which is called BEFORE this method. Do not call optimizeMaterialsForDynamicLights here.
  }
  
  /**
   * Optimize materials to prevent shader recompilation when dynamic lights are added.
   * This is called after level loading to:
   * 1. Pre-allocate light slots in materials (maxSimultaneousLights)
   * 
   * NOTE: Material freezing is disabled as it can cause rendering issues
   * if done before materials are fully ready (textures loaded, etc.)
   */
  private optimizeMaterialsForDynamicLights(maxLights: number): void {
      let lightSlotsConfigured = 0;
      
      for (const material of this.scene.materials) {
          // Set maxSimultaneousLights to match culling limit
          // This prevents shader recompilation when lights are enabled/disabled
          if ('maxSimultaneousLights' in material) {
              const stdMat = material as StandardMaterial;
              if (stdMat.maxSimultaneousLights !== maxLights) {
                  stdMat.maxSimultaneousLights = maxLights;
                  lightSlotsConfigured++;
              }
          }
          
          // NOTE: Freezing materials is risky - it can break rendering if done
          // before textures are loaded or if materials need updates later.
          // The includedOnlyMeshes approach on dynamic lights is safer.
      }
      
      logger.level.log(`🔧 Material optimization: ${lightSlotsConfigured} materials set to maxLights=${maxLights}`);
  }
  
  /**
   * Force all materials to compile their shaders upfront.
   * This prevents visual glitches (frame drops, flicker) when new meshes
   * come into view and their shaders are compiled on-demand.
   */
  private async warmupShaders(): Promise<void> {
      const materials = this.scene.materials;
      // CRITICAL: Include ALL meshes, even disabled ones (hidden weapons, projectile templates)
      // These meshes may become visible at runtime and need pre-compiled shaders.
      const allMeshes = this.scene.meshes.filter(m => !m.isDisposed());
      // Keep enabled meshes for priority matching
      const enabledMeshes = allMeshes.filter(m => m.isEnabled());
      
      if (materials.length === 0) {
          logger.level.debug('🔥 No materials to warmup');
          return;
      }
      
      logger.level.log(`🔥 Warming up shaders for ${materials.length} materials (${allMeshes.length} meshes, ${enabledMeshes.length} enabled)...`);
      const startTime = performance.now();
      
      // Force compilation of all materials
      // CRITICAL: Compile BOTH instanced and non-instanced variants!
      // Level geometry uses InstancedMesh, so we need the instanced shader variant.
      const compilationPromises: Promise<void>[] = [];
      
      for (const material of materials) {
          // Skip disposed materials - check via getScene() returning null
          if (!material.getScene()) continue;
          
          // Find a mesh that uses this material for compilation context
          // Try enabled meshes first, then fall back to any mesh (hidden weapons/projectiles)
          let meshWithMaterial = enabledMeshes.find(m => m.material === material);
          if (!meshWithMaterial) {
              meshWithMaterial = allMeshes.find(m => m.material === material);
          }
          
          // Only compile if we have a valid mesh - forceCompilation requires mesh context
          if (meshWithMaterial && meshWithMaterial.subMeshes && meshWithMaterial.subMeshes.length > 0) {
              // Compile NON-INSTANCED variant (for regular meshes)
              compilationPromises.push(
                  new Promise<void>((resolve) => {
                      try {
                          material.forceCompilation(meshWithMaterial!, () => {
                              resolve();
                          }, { clipPlane: false, useInstances: false });
                      } catch {
                          resolve();
                      }
                  })
              );
              
              // Compile INSTANCED variant (for InstancedMesh geometry)
              compilationPromises.push(
                  new Promise<void>((resolve) => {
                      try {
                          material.forceCompilation(meshWithMaterial!, () => {
                              resolve();
                          }, { clipPlane: false, useInstances: true });
                      } catch {
                          resolve();
                      }
                  })
              );
          }
          // Skip materials without meshes - they can't be compiled without mesh context
      }
      
      // Wait for all compilations to complete
      await Promise.all(compilationPromises);
      
      // NOTE: Do NOT freeze materials here - freezing before textures are fully loaded
      // or before the first render can break rendering completely.
      // The shader recompilation issue needs a different solution.
      
      const elapsed = (performance.now() - startTime).toFixed(1);
      logger.level.log(`🔥 Shader warmup complete: ${materials.length} materials in ${elapsed}ms`);
  }
  
  /**
   * Check if a mesh should be excluded from spatial culling.
   */
  private shouldSkipCulling(mesh: AbstractMesh): boolean {
      // Skip if already disabled or disposed
      if (!mesh.isEnabled() || mesh.isDisposed()) return true;
      
      // Skip the root mesh
      if (mesh.name === '__root__') return true;
      
      // Skip debug meshes
      if (mesh.metadata?.isDebug === true) return true;
      
      // Skip meshes marked with hm_no_cull
      const extras = this.getExtras(mesh);
      const noCull = getEntityProperty<boolean>(extras, 'no_cull', false);
      if (noCull === true) return true;
      
      // Skip skybox/environment meshes (behavior-based)
      const behaviorId = getEntityProperty<string>(extras, 'behavior_id', '');
      if (behaviorId === 'skybox' || behaviorId === 'environment') return true;
      
      // Skip UI/HUD related meshes (must have hm_is_hud annotation)
      const isHud = getEntityProperty<boolean>(extras, 'is_hud', false);
      if (isHud === true) return true;
      
      // Skip particle emitters - their particles can travel far, and disabling the mesh stops emission.
      // The emitter mesh is already invisible so there's no benefit to culling it.
      const behaviorsStr = getEntityProperty<string>(extras, 'behaviors', '');
      if (behaviorsStr.includes('emitter:particle')) return true;
      const isCharacter = behaviorsStr.split(',').some(b => b.trim().startsWith('character:'));
      if (isCharacter) return true;
      
      // Check if any parent is a character
      let parent = mesh.parent;
      while (parent) {
          const parentExtras = this.getExtras(parent);
          const parentBehaviors = getEntityProperty<string>(parentExtras, 'behaviors', '');
          if (parentBehaviors.split(',').some(b => b.trim().startsWith('character:'))) return true;
          parent = parent.parent;
      }
      
      return false;
  }

  /**
   * Process material effects on meshes (lava shader, video textures)
   * Material effects are configured via hm_materialLava_*, hm_materialVideo_* properties on MATERIALS
   * Particle emitters use hm_behaviors on OBJECTS
   */
  private processMaterialBehaviors(meshes: AbstractMesh[]): void {
    // Track processed materials to avoid duplicates
    const processedMaterials = new Set<string>();
    
    for (const mesh of meshes) {
      const matExtras = (mesh.material as any)?.metadata?.gltf?.extras;
      
      // Check material extras for VIDEO texture
      if (matExtras && matExtras.hm_materialVideo_enabled && mesh.material) {
        const matName = mesh.material.name;
        if (!processedMaterials.has(`video_${matName}`)) {
          processedMaterials.add(`video_${matName}`);
          
          const videoConfig = extractVideoConfigFromMesh(mesh);
          if (videoConfig) {
            logger.level.log(`🎬 Found video material on ${mesh.name} (material: ${matName}): ${videoConfig.src}`);
            
            // No try-catch - throw on error!
            applyVideoMaterialToMesh(mesh, videoConfig);
            if (videoConfig.autoplay) {
              playVideoOnMesh(mesh);
            }
          }
        }
      }
      
      // Check material extras for LAVA shader
      if (matExtras && matExtras.hm_materialLava_enabled && mesh.material) {
        const matName = mesh.material.name;
        if (!processedMaterials.has(`lava_${matName}`)) {
          processedMaterials.add(`lava_${matName}`);
          logger.level.log(`🌋 Found lava material on ${mesh.name} (material: ${matName})`);
          
          // Get the lava behavior and apply it
          // No try-catch - throw on error!
          const { behavior, resolvedId } = BehaviorRegistry.get('material:lava', mesh.name);
          if (behavior.onSpawn) {
            const entityId = `lava_${mesh.name}`;
            const spawnApi = {
              self: {
                id: entityId,
                physicsBodyId: entityId,
                type: EntityType.DYNAMIC_OBJECT,
                behaviorId: resolvedId,
                isPlayer: false,
                mesh,
              },
              config: matExtras,
              position: mesh.getAbsolutePosition().clone(),
              actions: {
                spawnParticles: () => {},
                playSound: () => {},
                sendEvent: () => {},
                getActor: () => undefined,
              },
              services: {} as any,
            };
            behavior.onSpawn(spawnApi);
          }
        }
      }
      
      // Check material extras OR mesh extras for WATER shader
      // Water properties can be on either the material or the mesh object in Blender
      const meshExtras = this.getExtras(mesh);
      const waterEnabled = matExtras?.hm_materialWater_enabled || meshExtras?.hm_materialWater_enabled;
      if (waterEnabled && mesh.material) {
        const matName = mesh.material.name;
        if (!processedMaterials.has(`water_${mesh.name}`)) {
          processedMaterials.add(`water_${mesh.name}`);
          logger.level.log(`💧 Found water material on ${mesh.name} (material: ${matName})`);
          
          // Get the water behavior and apply it
          // No try-catch - throw on error!
          const { behavior, resolvedId } = BehaviorRegistry.get('material:water', mesh.name);
          if (behavior.onSpawn) {
            const entityId = `water_${mesh.name}`;
            const spawnApi = {
              self: {
                id: entityId,
                physicsBodyId: entityId,
                type: EntityType.DYNAMIC_OBJECT,
                behaviorId: resolvedId,
                isPlayer: false,
                mesh,
              },
              config: matExtras,
              position: mesh.getAbsolutePosition().clone(),
              actions: {
                spawnParticles: () => {},
                playSound: () => {},
                sendEvent: () => {},
                getActor: () => undefined,
              },
              services: {} as any,
            };
            behavior.onSpawn(spawnApi);
          }
        }
      }
      
      // Check for particle emitters (uses hm_behaviors on object)
      const extras = this.getExtras(mesh);
      logger.probe('level:particle:check', `${mesh.name} | has extras=${extras != null}`);
      if (!extras) continue;
      
      const behaviors = getEntityProperty<string>(extras, 'behaviors', '');
      logger.probe('level:particle:behaviors', `${mesh.name} | behaviors=${behaviors}`);
      if (behaviors.includes('emitter:particle')) {
        logger.level.log(`✨ Found particle emitter: ${mesh.name}`);
        
        // No try-catch - throw on error!
        const { behavior, resolvedId } = BehaviorRegistry.get('emitter:particle', mesh.name);
        if (behavior.onSpawn) {
          const entityId = `particle_${mesh.name}`;
          const spawnApi = {
            self: {
              id: entityId,
              physicsBodyId: entityId,
              type: EntityType.PARTICLE_EMITTER,
              behaviorId: resolvedId,
              isPlayer: false,
              mesh,
            },
            config: extras,
            position: mesh.getAbsolutePosition().clone(),
            actions: {
              spawnParticles: () => {},
              playSound: () => {},
              sendEvent: () => {},
              getActor: () => undefined,
            },
            services: {} as any,
          };
          behavior.onSpawn(spawnApi);
        }
      }
    }
  }

  // @ts-expect-error - Kept for potential debug/fallback use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _createFallbackLevel(): void {
    logger.level.warn('⚠️ Using fallback level generation');
    // Create ground - use a box instead of ground plane for better collision
    const ground = MeshBuilder.CreateBox('ground', { width: 20, height: 0.5, depth: 10 }, this.scene);
    ground.position.y = -0.25;  // Bottom at y=0, top at y=0
    const groundMaterial = new StandardMaterial('groundMat', this.scene);
    groundMaterial.diffuseColor = new Color3(0.3, 0.6, 0.3);
    ground.material = groundMaterial;
    ground.checkCollisions = true;
    this.physicsManager.createStaticBox('ground', ground, 1.0);

    // Create platforms
    this.createPlatform('platform1', new Vector3(-5, 1, 0), new Color3(0.7, 0.4, 0.2));
    this.createPlatform('platform2', new Vector3(0, 2.5, 0), new Color3(0.6, 0.5, 0.3));
    this.createPlatform('platform3', new Vector3(5, 4, 0), new Color3(0.5, 0.6, 0.4));

    // Visual landmarks
    this.createLandmark('landmark1', new Vector3(-8, 0.5, 0), new Color3(1, 0, 0));
    this.createLandmark('landmark2', new Vector3(8, 0.5, 0), new Color3(0, 0, 1));

    // Create boundary walls
    this.createWall('leftWall', new Vector3(-10, 0.5, 0));
    this.createWall('rightWall', new Vector3(10, 0.5, 0));
  }

  private createPlatform(name: string, position: Vector3, color: Color3): Mesh {
    const platform = MeshBuilder.CreateBox(name, { width: 3, height: 0.5, depth: 3 }, this.scene);
    platform.position = position;
    const material = new StandardMaterial(`${name}Mat`, this.scene);
    material.diffuseColor = color;
    platform.material = material;
    platform.checkCollisions = true;
    this.physicsManager.createStaticBox(name, platform, 1.0);
    return platform;
  }

  private createLandmark(name: string, position: Vector3, color: Color3): Mesh {
    const landmark = MeshBuilder.CreateBox(name, { size: 1 }, this.scene);
    landmark.position = position;
    const material = new StandardMaterial(`${name}Mat`, this.scene);
    material.diffuseColor = color;
    landmark.material = material;
    return landmark;
  }

  private createWall(name: string, position: Vector3): Mesh {
    const wallHeight = 1.0;
    const wallThickness = 0.5;
    const wall = MeshBuilder.CreateBox(name, { 
      width: wallThickness, 
      height: wallHeight, 
      depth: 10 
    }, this.scene);
    // Adjust Y position to be centered vertically based on height
    // In main.ts it was: position.y = wallHeight / 2
    // The passed position has y=0.5 which is wallHeight/2, so we use it directly
    wall.position = position; 
    
    const material = new StandardMaterial(`${name}Mat`, this.scene);
    material.diffuseColor = new Color3(0.4, 0.3, 0.2);
    wall.material = material;
    wall.checkCollisions = true;
    this.physicsManager.createStaticBox(name, wall, 1.0);
    return wall;
  }

  /**
   * Get environment/skybox info for debug UI
   */
  getEnvironmentInfo(): any {
    return this.environmentManager.getInfo();
  }

  /**
   * Load HDR environment texture from URL
   */
  loadHdrFromUrl(url: string, envIntensity?: number, skyboxBrightness?: number): void {
    this.environmentManager.loadHdrFromUrl(url, envIntensity, skyboxBrightness);
  }

  /**
   * Set environment IBL intensity
   */
  setEnvIntensity(value: number): void {
    this.environmentManager.setEnvIntensity(value);
  }

  /**
   * Set skybox brightness
   */
  setSkyboxBrightness(value: number): void {
    this.environmentManager.setSkyboxBrightness(value);
  }

  /**
   * Handle object destruction: show debris, hide main mesh, schedule despawn
   */
  private handleObjectDestroyed(objectId: string, mesh: AbstractMesh): void {
    logger.assets.log(`💥 Object destroyed: ${objectId}, mesh: ${mesh.name}`);

    // Get debris meshes from metadata (stored during spawn) or search children
    let debrisMeshes: AbstractMesh[] = [];
    
    // First check metadata (preferred - set during asset spawn)
    if (mesh.metadata?._debrisMeshes && Array.isArray(mesh.metadata._debrisMeshes)) {
      debrisMeshes = mesh.metadata._debrisMeshes;
      logger.assets.debug(`   Found ${debrisMeshes.length} debris meshes from metadata`);
    } else {
      // Fallback: search all descendants for hm_mesh_role="debris"
      const allDescendants = mesh.getChildMeshes(true);
      logger.assets.debug(`   No debris in metadata, searching ${allDescendants.length} children`);
      for (const child of allDescendants) {
        const role = child.metadata?.gltf?.extras?.hm_mesh_role || 
                     child.metadata?.extras?.hm_mesh_role ||
                     (child.metadata as any)?.hm_mesh_role;
        if (role === 'debris') {
          debrisMeshes.push(child);
        }
      }
      if (debrisMeshes.length > 0) {
        logger.assets.debug(`   Found ${debrisMeshes.length} debris meshes from child search`);
      }
    }

    if (debrisMeshes.length > 0) {
      logger.assets.log(`   Found ${debrisMeshes.length} debris meshes - activating physics`);
      
      debrisMeshes.forEach((debris, index) => {
        // First enable the debris so its world matrix gets computed properly
        debris.setEnabled(true);
        debris.computeWorldMatrix(true);
        
        // Get the world transform BEFORE unparenting (using utility that works for both Mesh and InstancedMesh)
        const { position: worldPos, rotation: worldRotQ, scale: worldScale } = getWorldTransform(debris);
        const worldRot = worldRotQ.toEulerAngles();
        
        // Unparent debris so it can move independently
        debris.setParent(null);
        debris.position.copyFrom(worldPos);
        debris.rotation.copyFrom(worldRot);
        debris.scaling.copyFrom(worldScale);
        
        // Show the debris
        debris.isVisible = true;
        
        // Center geometry for physics (same approach as main physics mesh)
        if (debris instanceof Mesh) {
          debris.refreshBoundingInfo({ applySkeleton: true });
          const bbCenterLocal = debris.getBoundingInfo().boundingBox.center.clone();
          
          if (bbCenterLocal.x !== 0 || bbCenterLocal.y !== 0 || bbCenterLocal.z !== 0) {
            const positions = debris.getVerticesData('position');
            if (positions) {
              for (let i = 0; i < positions.length; i += 3) {
                positions[i] -= bbCenterLocal.x;
                positions[i + 1] -= bbCenterLocal.y;
                positions[i + 2] -= bbCenterLocal.z;
              }
              debris.setVerticesData('position', positions);
              
              // Move mesh position to keep geometry in place
              const offsetWorld = bbCenterLocal.multiply(debris.scaling);
              debris.position.addInPlace(offsetWorld);
              
              debris.refreshBoundingInfo({ applySkeleton: true });
            }
          }
        }
        
        // Create physics body for debris
        const debrisId = `${objectId}_debris_${index}`;
        this.physicsManager.createBody(
          debrisId,
          debris,
          PhysicsShapeType.BOX,
          {
            mass: 1.0, // Light debris
            friction: 0.5,
            restitution: 0.3,
            collisionGroup: CollisionGroups.DYNAMIC,
            collisionMask: CollisionMasks.DYNAMIC,
          }
        );
        
        // Apply random impulse to scatter debris
        if (debris.physicsImpostor) {
          const randomDir = new Vector3(
            (Math.random() - 0.5) * 2,
            Math.random() * 0.5 + 0.5, // Upward bias
            (Math.random() - 0.5) * 2
          ).normalize();
          const impulseStrength = 3 + Math.random() * 4; // 3-7 units
          debris.physicsImpostor.applyImpulse(
            randomDir.scale(impulseStrength),
            debris.getAbsolutePosition()
          );
          
          // Also apply random angular velocity for tumbling
          debris.physicsImpostor.setAngularVelocity(new Vector3(
            (Math.random() - 0.5) * 10,
            (Math.random() - 0.5) * 10,
            (Math.random() - 0.5) * 10
          ));
          
          logger.assets.log(`   💨 Applied impulse to debris ${index}: dir=${randomDir.toString()}, strength=${impulseStrength.toFixed(1)}`);
        }
      });
    }

    // Hide main render meshes (search all descendants)
    const allDescendants = mesh.getChildMeshes(true);
    for (const child of allDescendants) {
      const role = child.metadata?.gltf?.extras?.hm_mesh_role || 
                   child.metadata?.extras?.hm_mesh_role ||
                   (child.metadata as any)?.hm_mesh_role;
      if (role === 'render' || !role) {
        child.isVisible = false;
      }
    }

    // Hide the physics mesh itself (collision mesh)
    mesh.isVisible = false;

    // Disable physics on the main object
    if (mesh.physicsImpostor) {
      mesh.physicsImpostor.dispose();
    }

    // Schedule despawn after debris lifetime (default 5 seconds)
    const debrisLifetime = 5000;
    setTimeout(() => {
      this.despawnObject(objectId, mesh, debrisMeshes);
    }, debrisLifetime);
  }

  /**
   * Despawn object: remove from scene and physics
   */
  private despawnObject(objectId: string, mesh: AbstractMesh, debrisMeshes: AbstractMesh[] = []): void {
    logger.assets.log(`🗑️ Despawning object: ${objectId}`);

    // Dispose debris meshes and their physics
    for (const debris of debrisMeshes) {
      if (debris.physicsImpostor) {
        debris.physicsImpostor.dispose();
      }
      debris.dispose();
    }

    // Remove physics body from main mesh
    if (mesh.physicsImpostor) {
      mesh.physicsImpostor.dispose();
    }
    if (mesh.physicsBody) {
      mesh.physicsBody.dispose();
    }

    // Dispose all child meshes
    mesh.getChildMeshes().forEach((child) => {
      child.dispose();
    });

    // Dispose main mesh
    mesh.dispose();

    logger.assets.log(`   ✅ Object despawned: ${objectId}`);
  }
  
  /**
   * Unload the current level and dispose all loaded resources.
   * Call this before loading a new level to prevent memory leaks.
   */
  public unload(): void {
    logger.level.log('🧹 Unloading level resources...');
    
    // Get material count before for logging
    const materialsBefore = this.scene.materials.length;
    const texturesBefore = this.scene.textures.length;
    
    // Collect materials used by loaded meshes BEFORE disposing
    const materialsToDispose = new Set<import('@babylonjs/core').Material>();
    for (const mesh of this.loadedMeshes) {
      if (!mesh.isDisposed() && mesh.material) {
        materialsToDispose.add(mesh.material);
      }
    }
    
    // Stop and dispose animation groups first (they reference other objects)
    for (const animGroup of this.loadedAnimationGroups) {
      try {
        animGroup.stop();
        animGroup.dispose();
      } catch {
        // Already disposed
      }
    }
    this.loadedAnimationGroups = [];
    
    // Dispose loaded meshes (DON'T auto-dispose materials, we'll do it ourselves)
    for (const mesh of this.loadedMeshes) {
      if (!mesh.isDisposed()) {
        mesh.dispose(false, false); // Don't dispose parent, don't dispose materials
      }
    }
    this.loadedMeshes = [];
    
    // Dispose transform nodes
    for (const node of this.loadedTransformNodes) {
      if (!node.isDisposed()) {
        node.dispose();
      }
    }
    this.loadedTransformNodes = [];
    
    // Dispose materials that were used by loaded meshes
    for (const material of materialsToDispose) {
      // Check if material is still valid (getScene returns null if disposed)
      if (material && material.getScene()) {
        // Check if material is still in use by non-disposed meshes
        const isUsed = this.scene.meshes.some(m => !m.isDisposed() && m.material === material);
        if (!isUsed) {
          material.dispose(true, true); // Dispose textures and force dispose
        }
      }
    }
    
    // Clean up any orphaned materials (safety net)
    // Skip ShaderMaterial which may be used by post-processing systems
    const orphanedMaterials = this.scene.materials.filter(m => {
      const className = m.getClassName();
      // Skip shader materials used by systems
      if (className === 'ShaderMaterial') return false;
      // Check if any non-disposed mesh uses this material
      const isUsed = this.scene.meshes.some(mesh => !mesh.isDisposed() && mesh.material === m);
      return !isUsed;
    });
    for (const material of orphanedMaterials) {
      material.dispose(true, true); // Dispose textures too
    }
    
    // NOTE: We don't clean up orphaned textures directly because:
    // 1. Material.dispose(true) already handles texture disposal
    // 2. Skeleton bone textures appear as orphans but are actively used
    // 3. Character cache textures need to persist across level switches
    
    const materialsAfter = this.scene.materials.length;
    const texturesAfter = this.scene.textures.length;
    logger.level.log(`   Materials: ${materialsBefore} → ${materialsAfter}, Textures: ${texturesBefore} → ${texturesAfter}`);
    
    // Dispose default light
    if (this.defaultLight) {
      this.defaultLight.dispose();
      this.defaultLight = null;
    }
    
    // Clear triggers
    if (this.triggerSystem) {
      this.triggerSystem.clear();
    }
    
    // Clear dynamic objects
    if (this.dynamicObjectSystem) {
      this.dynamicObjectSystem.clear();
    }
    
    // Clear light requests (managed lights)
    UnifiedLightManager.clearAllRequests();
    
    // Clear spatial culling
    const spatialCulling = ServiceRegistry.getSpatialCulling();
    if (spatialCulling) {
      spatialCulling.dispose();
    }
    
    // Reset environment
    this.environmentManager.clear();
    
    // Clear post-process god rays (they reference disposed meshes)
    if (this.postProcessManager) {
      this.postProcessManager.clearGodRays();
    }
    
    // Clear last level data
    this.lastLevelData = null;
    
    logger.level.log('✅ Level resources unloaded');
  }
}